import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useApiAuth } from '../contexts/ApiAuthContext';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../constants/brandPalette';
import unauthorizedImg from '../assets/images/unauthorized.jpg';
import logo3d from '../assets/logos/3D-LOGO.png';

const COUNTDOWN_SECONDS = 10;

const CountdownRing = ({ value, max }) => {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / max) * circ;
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: 80, height: 80 }}>
      <svg width="80" height="80" style={{ transform: 'rotate(-90deg)', position: 'absolute', top: 0, left: 0 }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke={SYSTEM_COLORS.borderSoft} strokeWidth="5" />
        <circle
          cx="40" cy="40" r={r} fill="none"
          stroke={SYSTEM_COLORS.danger} strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.95s linear' }}
        />
      </svg>
      <span className="text-2xl font-bold tabular-nums" style={{ color: SYSTEM_COLORS.danger }}>
        {value}
      </span>
    </div>
  );
};

const UnauthorizedPage = () => {
  const { logout } = useApiAuth();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const calledRef = useRef(false);

  const handleLogout = useCallback(async () => {
    if (calledRef.current) return;
    calledRef.current = true;
    try { await logout(); } catch {}
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [handleLogout]);

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: SYSTEM_COLORS.shellBackground }}>

      {/* ── Left panel — brand + illustration (desktop only) ── */}
      <div
        className="hidden lg:flex flex-col items-center justify-center flex-1 px-12 relative overflow-hidden"
        style={{ backgroundColor: BRAND_COLORS.primary }}
      >
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(255,255,255,0.07) 0%, transparent 70%)' }}
        />

        {/* Brand */}
        <div className="flex items-center gap-3 mb-10 z-10">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
            <img src={logo3d} alt="Logo" className="w-full h-full object-cover" />
          </div>
          <span className="text-2xl font-bold text-white tracking-wide">A.M.A.R.M.S</span>
        </div>

        {/* Illustration */}
        <div className="relative z-10 mb-10">
          <div
            className="flex items-center justify-center rounded-3xl overflow-hidden"
            style={{
              width: 300, height: 300,
              backgroundColor: '#ffffff',
              boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }}
          >
            <img
              src={unauthorizedImg}
              alt="Unauthorized access"
              className="w-full h-full object-contain p-8"
            />
          </div>
        </div>

        <p className="text-sm text-center max-w-xs leading-relaxed z-10"
          style={{ color: 'rgba(255,255,255,0.65)' }}>
          Security is our priority. All access attempts are continuously monitored and logged.
        </p>
      </div>

      {/* ── Right panel — content ── */}
      <div className="flex flex-1 items-center justify-center px-6 py-12"
        style={{ backgroundColor: SYSTEM_COLORS.shellBackground }}>

        <div className="w-full max-w-sm">

          {/* Mobile: brand + illustration */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0"
                style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}>
                <img src={logo3d} alt="Logo" className="w-full h-full object-cover" />
              </div>
              <span className="text-lg font-bold tracking-wide" style={{ color: BRAND_COLORS.primary }}>
                A.M.A.R.M.S
              </span>
            </div>
            <div
              className="flex items-center justify-center rounded-2xl overflow-hidden"
              style={{ width: 200, height: 200, backgroundColor: SYSTEM_COLORS.dangerTint }}
            >
              <img
                src={unauthorizedImg}
                alt="Unauthorized access"
                className="w-full h-full object-contain p-5"
              />
            </div>
          </div>

          {/* Eyebrow */}
          <p className="text-xs font-semibold uppercase tracking-[0.18em] mb-2"
            style={{ color: SYSTEM_COLORS.danger }}>
            Security Alert
          </p>

          {/* Heading */}
          <h1 className="text-3xl font-bold mb-4" style={{ color: NEUTRAL_COLORS.black }}>
            Out of Scope
          </h1>

          <p className="text-sm font-medium mb-2" style={{ color: NEUTRAL_COLORS.black }}>
            You tried to access a page that is outside your permitted scope.
          </p>

          <p className="text-sm mb-6" style={{ color: SYSTEM_COLORS.textMuted }}>
            To keep you safe, we are signing you out. If this was an accident,
            please log in again normally.
          </p>

          {/* Warning pill */}
          <div
            className="flex items-start gap-3 px-4 py-3 rounded-xl mb-8"
            style={{ backgroundColor: SYSTEM_COLORS.dangerTint }}
          >
            <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" style={{ color: SYSTEM_COLORS.danger }} />
            <p className="text-xs leading-relaxed font-medium" style={{ color: SYSTEM_COLORS.danger }}>
              Repeating this action may result in your account and device being permanently deactivated.
            </p>
          </div>

          {/* Countdown ring + label */}
          <div className="flex items-center gap-4 mb-8 px-4 py-4 rounded-xl"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, border: `1px solid ${SYSTEM_COLORS.borderSoft}` }}>
            <CountdownRing value={countdown} max={COUNTDOWN_SECONDS} />
            <div>
              <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                Auto sign-out
              </p>
              <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                You will be signed out in <span className="font-semibold tabular-nums"
                  style={{ color: SYSTEM_COLORS.danger }}>{countdown}</span> second{countdown !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="mb-5" style={{ height: 1, backgroundColor: SYSTEM_COLORS.borderSoft }} />

          {/* Sign out button — only action, no back */}
          <button
            onClick={handleLogout}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80"
            style={{ backgroundColor: SYSTEM_COLORS.danger }}
          >
            Sign Out Now
          </button>
        </div>

      </div>
    </div>
  );
};

export default UnauthorizedPage;
